import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Plus, Phone, Mail, UserPlus, Target, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const stages = ['new', 'contacted', 'meeting', 'trial', 'enrolled', 'lost'];
const stageColors = {
  new: 'bg-blue-100 text-blue-700', contacted: 'bg-purple-100 text-purple-700',
  meeting: 'bg-amber-100 text-amber-700', trial: 'bg-cyan-100 text-cyan-700',
  enrolled: 'bg-emerald-100 text-emerald-700', lost: 'bg-red-100 text-red-700'
};

export default function AcademyCRM() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', source: 'walk-in', stage: 'new', notes: '' });

  const fetchProspects = useCallback(async () => {
    try {
      const res = await api.get('/academy/prospects').catch(() => ({ data: [] }));
      setProspects(res.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/academy/prospects', form);
      toast.success('Prospect added');
      setShowAdd(false);
      setForm({ name: '', phone: '', email: '', source: 'walk-in', stage: 'new', notes: '' });
      fetchProspects();
    } catch (err) { toast.error('Failed'); }
  };

  const handleUpdateStage = async (id, stage) => {
    try {
      await api.put(`/academy/prospects/${id}`, { stage });
      toast.success(`Updated to ${stage}`);
      fetchProspects();
    } catch (err) { toast.error('Failed'); }
  };

  const enrolled = prospects.filter(p => p.stage === 'enrolled').length;
  const active = prospects.filter(p => !['enrolled', 'lost'].includes(p.stage)).length;

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64 rounded-xl" /></div>;

  return (
    <div className="space-y-6" data-testid="academy-crm-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Academy CRM</h1>
          <p className="text-sm text-muted-foreground">Track leads and convert to students</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> Add Prospect</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add New Prospect</DialogTitle></DialogHeader>
            <form onSubmit={handleAdd} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Name *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Parent/Student name" required /></div>
                <div className="space-y-1.5"><Label className="text-xs">Phone *</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+91 98765..." required /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="email@example.com" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Source</Label>
                  <Select value={form.source} onValueChange={(v) => setForm({...form, source: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                    {['walk-in', 'referral', 'social-media', 'website', 'event', 'other'].map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('-', ' ')}</SelectItem>)}
                  </SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Stage</Label>
                  <Select value={form.stage} onValueChange={(v) => setForm({...form, stage: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                    {stages.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent></Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Interested in U12 Football" /></div>
              <Button type="submit" className="w-full">Add Prospect</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Active Leads</span><Target className="h-4 w-4 text-blue-600" /></div><p className="text-2xl font-display font-semibold tabular-nums">{active}</p></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Enrolled</span><UserPlus className="h-4 w-4 text-emerald-600" /></div><p className="text-2xl font-display font-semibold tabular-nums">{enrolled}</p></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Conversion</span><TrendingUp className="h-4 w-4 text-primary" /></div><p className="text-2xl font-display font-semibold tabular-nums">{prospects.length > 0 ? Math.round(enrolled / prospects.length * 100) : 0}%</p></CardContent></Card>
      </div>

      {/* Prospects Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead className="hidden md:table-cell">Source</TableHead><TableHead>Stage</TableHead><TableHead className="hidden md:table-cell">Notes</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {prospects.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No prospects yet. Add your first lead to start tracking.</TableCell></TableRow>
              ) : prospects.map(p => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium text-sm">{p.name}</TableCell>
                  <TableCell><div className="text-xs"><div className="flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone}</div>{p.email && <div className="flex items-center gap-1 text-muted-foreground"><Mail className="h-3 w-3" />{p.email}</div>}</div></TableCell>
                  <TableCell className="hidden md:table-cell"><Badge variant="outline" className="text-xs capitalize">{p.source?.replace('-', ' ')}</Badge></TableCell>
                  <TableCell>
                    <Select value={p.stage} onValueChange={(v) => handleUpdateStage(p.id, v)}>
                      <SelectTrigger className="h-7 w-28"><span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium capitalize ${stageColors[p.stage] || 'bg-slate-100'}`}>{p.stage}</span></SelectTrigger>
                      <SelectContent>{stages.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-muted-foreground">{p.notes || '-'}</TableCell>
                  <TableCell>
                    {p.stage !== 'enrolled' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleUpdateStage(p.id, 'enrolled')}><UserPlus className="h-3 w-3 mr-1" /> Enroll</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
